import TransactionModal from "./transactionModal.js";
import { $_GET, trans } from "../globalFunctions.js";

export default class SingleGrantModal extends TransactionModal {
  constructor(companyId) {
    super("single-grant-modal", companyId);

    this.pools = {};
    this.VESTING_TYPE_SIMPLE = 1;
    this.$equityPlan = this.$form.querySelector('[name="plan"]');
    this.$availableShares = this.$form.querySelector("[data-available-shares]");
    this.$amount = this.$form.querySelector('[name="amount"]');
    this.$date = this.$form.querySelector('[name="date"]');
    this.formFields = [
      {
        name: "id",
        varType: "int",
      },
      {
        name: "stakeholder",
        varType: "int",
        required: true,
        trigger: "change",
      },
      {
        name: "beneficiary",
        varType: "int",
      },
      {
        name: "amount",
        varType: "float",
        required: true,
      },
      {
        name: "plan",
        varType: "int",
        required: true,
      },
      {
        name: "date",
        varType: "string",
        required: true,
      },
      {
        name: "purchase_price",
        varType: "float",
      },
      {
        name: "strike_price",
        varType: "float",
      },
      {
        name: "documents",
        varType: "simple-array",
        type: "int",
      },
      {
        name: "notes",
      },
      {
        name: "vesting_type",
        varType: "int",
        trigger: "change",
      },
      {
        name: "vesting_duration",
        varType: "int",
      },
      {
        name: "vesting_interval",
        varType: "int",
      },
      {
        name: "vesting_cliff",
        varType: "int",
      },
      {
        name: "vesting_date",
        varType: "string",
      },
      {
        name: "expiration_date",
        varType: "string",
        mayNotExist: true,
      },
      {
        name: "good_leaver",
        varType: "string",
      },
      {
        name: "bad_leaver",
        varType: "string",
      },
      {
        name: "liquidity_event",
        varType: "string",
      },
      {
        name: "notify",
        fieldType: "checkbox",
      },
    ];

    this.setupForm();

    Promise.all([
      this.documentsPromise,
      this.setupStakeholders(),
      this.setupPlans(),
    ]).then(() => {
      this.whenReady();
    });
  }

  whenReady() {
    super.whenReady();

    if (this.currentData === null) {
      this.applyPlan(this.$equityPlan.value, false);
      this.$date.valueAsDate = new Date(); // otherwise plan's date gets applied...
    } else {
      this.applyPlan(this.currentData.plan, true);
      this.showVestingFields(this.currentData.vesting_type);
    }
  }

  getStakeholders() {
    // override  getStakeholders function to have all stakeholders data
    return new Promise((resolve, reject) => {
      peque
        .api("stakeholders", { company: this.companyId }, "get")
        .then((response) => {
          this.stakeholders = response.result.list;
          resolve(this.stakeholders);
        });
    });
  }

  getSelectedPlan() {
    return this.$equityPlan.value;
  }

  setupForm() {
    this.$date.valueAsDate = new Date();
    const $vestingType = this.$form.querySelector('[name="vesting-type"]');
    $vestingType.addEventListener("change", () => {
      this.showVestingFields($vestingType.value);
    });

    this.$date.addEventListener("change", () => {
      if (typeof this.dateChangeTimeout !== "undefined") {
        clearTimeout(this.dateChangeTimeout);
      }
      this.dateChangeTimeout = setTimeout(() => {
        const plan = this.plans[this.getSelectedPlan()];
        this.calculateAvailableSharesByDate(plan.pool);
        this.getLatestValuation();
      }, 1000);
    });

    this.$equityPlan.addEventListener("change", () => {
      this.applyPlan(this.$equityPlan.value);
    });

    this.$amount.addEventListener("input", () => {
      let amount = this.getFloat("amount");

      if (isNaN(amount)) {
        this.setFloat("amount", 1);
        this.setAvailableSharesPreview(this.availableSharesCount - 1);
        return;
      }

      this.setAvailableSharesPreview(this.availableSharesCount - amount);

      if (
        typeof this.$amount.max === "undefined" ||
        this.$amount.max.length < 1
      ) {
        return;
      }

      if (amount > parseInt(this.$amount.max)) {
        this.setFloat("amount", this.$amount.max);
        this.setAvailableSharesPreview(0);
      }
    });

    // if user creates a new stakeholder, add it to the list
    document.body.addEventListener("new-stakeholder", (e) => {
      this.stakeholders.push(e.detail);
    });
  }

  applyPlan(planId, fromPrefill) {
    planId = parseInt(planId);

    if (isNaN(planId)) {
      return;
    }
    const grantType = this.plans[planId].grant_type; // plan var may not be the plan
    const plan = fromPrefill !== true ? this.plans[planId] : this.currentData;

    this.calculateAvailableSharesByDate(this.plans[planId].pool);

    if (plan.vesting_type > 0) {
      const $vestingType = this.$form.querySelector('[name="vesting-type"]');
      $vestingType.value = plan.vesting_type;
      $vestingType.dispatchEvent(new Event("change"));
    }

    const $strikePrice = this.$form
      .querySelector('[name="strike-price"]')
      .closest(".form-group").parentElement;
    const $purchasePrice = this.$form
      .querySelector('[name="purchase-price"]')
      .closest(".form-group").parentElement;
    const $expirationDate = document.querySelector(
      '[name="expiration-date"]'
    ).parentElement;
    const $ppsPriceLabel = this.$form.querySelector(
      '[data-id="pps-price-label"]'
    );
    const $strikePriceLabel = this.$form.querySelector(
      '[data-id="strike-price-label"]'
    );
    const $purchasePriceDefinition = this.$form.querySelector(
      '[data-id="purchase-price-definition"]'
    );

    $strikePrice.classList.add("d-none");
    $purchasePrice.classList.add("d-none");
    $ppsPriceLabel.classList.add("d-none");
    $purchasePriceDefinition.classList.add("d-none");
    $strikePriceLabel.classList.remove("d-none");

    switch (grantType) {
      case peque.GRANT_TYPE_PHANTOM_SHARES:
        $purchasePrice.classList.remove("d-none");
        break;
      case peque.GRANT_TYPE_SARS:
        $strikePrice.classList.remove("d-none");
        $purchasePrice.classList.remove("d-none");
        $ppsPriceLabel.classList.remove("d-none");
        $ppsPriceLabel.classList.remove("d-none");
        $purchasePriceDefinition.classList.remove("d-none");
        $strikePriceLabel.classList.add("d-none");

        if (fromPrefill !== true) {
          this.getLatestValuation();
        }
        break;
      case peque.GRANT_TYPE_STOCK:
      case peque.GRANT_TYPE_WARRANTS:
      default:
        $strikePrice.classList.remove("d-none");
        $purchasePrice.classList.remove("d-none");
        break;
    }
    if (grantType) {
      $expirationDate.classList.remove("d-none");
    } else {
      $expirationDate.classList.add("d-none");
    }

    if (fromPrefill !== true) {
      const planClone = Object.assign({}, plan);
      delete planClone.id;
      delete planClone.date; // avoid overriding grant's date
      this.prefillForm(planClone, this.formFields);
    } else {
      if (!isNaN(this.currentData.vesting_cliff)) {
        this.$form.querySelector('[name="vesting-cliff"]').value =
          this.currentData.vesting_cliff;
      }
    }
  }

  getLatestValuation() {
    return new Promise((resolve, reject) => {
      peque
        .api(
          "valuations/latest",
          {
            company: this.companyId,
            simulation: $_GET("simulation"),
            date: this.$date.value,
          },
          "get"
        )
        .then((response) => {
          this.setFloat(
            "strike-price",
            response.result ? response.result.private.price : 0
          );
          resolve();
        });
    });
  }

  calculateAvailableSharesByDate(poolId) {
    poolId = parseInt(poolId);

    peque
      .api(
        "shares/count",
        {
          receiver_id: poolId,
          receiver_type: peque.SHARE_RECEIVER_TYPE_POOL,
          company: this.companyId,
          date: this.$date.value,
        },
        "get"
      )
      .then((response) => {
        this.availableSharesCount =
          response.result +
          (this.currentData ? Math.abs(this.currentData.amount) : 0);

        this.availableSharesCount -= this.getFloat("amount");

        this.setAvailableSharesPreview(this.availableSharesCount);
        this.$amount.max = this.availableSharesCount;
      });
  }

  setAvailableSharesPreview(amount) {
    this.$availableShares.querySelector("i").innerText =
      peque.formatNumber(amount);
  }

  setupPlans() {
    return new Promise((resolve, reject) => {
      peque
        .api("equity_plans", { company: this.companyId }, "get")
        .then((response) => {
          if (response.error > 0) {
            reject();
            return;
          }

          this.plans = {};

          response.result.list.forEach((entry) => {
            const el = document.createElement("option");
            el.innerText = entry.name;
            el.value = entry.id;
            this.$equityPlan.appendChild(el);

            this.plans[entry.id] = entry;
          });

          if (response.result.list.length === 0) {
            peque.navigation
              .showError(trans("create_a_plan_first"))
              .then(() => {
                window.location = peque.routeFor("plansList");
              });
          }

          new AutocompleteSelect(this.$equityPlan, {
            inputClasses: ["form-control"],
            dropdownClasses: ["border"],
          });
          resolve(response.result.list);
        });
    });
  }

  showVestingFields(type) {
    type = parseInt(type);
    this.$form.querySelectorAll("[data-vesting-child]").forEach((el) => {
      switch (type) {
        case this.VESTING_TYPE_SIMPLE:
          el.classList.remove("d-none");
          break;
        default:
          el.classList.add("d-none");
          break;
      }
    });
  }

  getStakeholder(id) {
    for (let i = 0; i < this.stakeholders.length; i++) {
      const stakeholder = this.stakeholders[i];

      if (stakeholder.id === id) {
        return stakeholder;
      }
    }
    return null;
  }

  showStakeholderModal(selectedStakeholder, resolve) {
    // since this modal is also used by send test modal, can't set the callback after new StakeholderModal(),
    // as it may already be defined
    if (typeof peque.stakeholderModal === "undefined") {
      peque.stakeholderModal = new StakeholderModal();
    }
    if (typeof this.stakeholderHideSet === "undefined") {
      peque.stakeholderModal.$modal._element.addEventListener(
        "hide.bs.modal",
        (event) => {
          this.enableSubmitButton(true);
        }
      );
      this.stakeholderHideSet = true;
    }
    peque.stakeholderModal.setCallback((stakeholder) => {
      peque.stakeholderModal.hide();
      resolve(stakeholder);
    });
    peque.stakeholderModal.setPrefillData(selectedStakeholder);
    peque.stakeholderModal.show();
  }

  validateAndSendForm() {
    this.enableSubmitButton(false);

    this.getFields(this.formFields)
      .then((data) => {
        // wants to notify the receiver, but it doesn't have a mail set
        if (data.notify) {
          const stakeholder = this.getStakeholder(data.stakeholder);

          if (stakeholder.email === null || stakeholder.email.length < 1) {
            const message = trans(
              "stakeholdername_does_not_have_an_email_set_do_you_want_to_add_it_now",
              {
                stakeholderName: stakeholder.name,
              }
            );
            peque.navigation
              .showConfirm(message, trans("warning"))
              .then((answer) => {
                if (!answer.isConfirmed) {
                  // on cancel, disabled sent notification and trigger submit again
                  document.getElementById(
                    "send-email-notification"
                  ).checked = false;
                  this.validateAndSendForm();
                  return;
                }
                this.showStakeholderModal(stakeholder, (data) => {
                  stakeholder.email = data.email;
                  this.validateAndSendForm();
                });
              });
            return;
          }
        }

        if (data.vesting_cliff && data.vesting_duration < data.vesting_cliff) {
          const message = trans(
            "vesting_duration_can_t_be_lower_than_vesting_cliff"
          );
          peque.navigation.showError(message);
          this.showErrorOnField(
            message,
            this.$form.querySelector('[name="vesting-duration"]')
          );
          this.enableSubmitButton(true);
          return;
        }

        data.company = this.companyId;
        data.type = peque.TRANSACTION_TYPE_GRANT;

        peque
          .api("transactions", JSON.stringify(data), "post")
          .then((response) => {
            this.mutateReactTransactions();
          })
          .catch((error) => {
            this.enableSubmitButton(true);

            if (
              error.error ===
              peque.ERROR_THERE_ARE_MORE_GRANTED_SHARES_THAN_GIVEN_VALUE
            ) {
              peque.navigation.showError(
                trans("pool_shares_in_use_by_future_grants")
              );
              return;
            }

            if (typeof error.field !== "undefined") {
              this.showErrorOnField(
                error.result,
                this.$form.querySelector('[name="' + error.field + '"]')
              );
            }
          });
      })
      .catch((error) => {
        this.enableSubmitButton(true);

        if (typeof error.el === "undefined") {
          throw error;
        }
        this.showErrorOnField(error.message, error.el);
      });
  }
}
